import React, { useCallback, useRef, useState } from 'react';
import Head from 'expo-router/head';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from 'react-native';
import { Link } from 'expo-router';
import { useFocusEffect } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme, TYPOGRAPHY, SPACING, NARROW_MAX_WIDTH, withWeight } from '../../src/theme';
import { ContentConstraint } from '@components/layout';
import { Button, ErrorBanner, Input, RediLogo } from '@components/common';
import { GoogleSignInButton } from '@components/auth/GoogleSignInButton';
import { useAuthStore } from '../../src/store/authStore';
import { appAlert } from '../../src/utils/appAlert';

export default function SignInScreen() {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { signIn, signInWithGoogle, sendPasswordResetEmail, isLoading, error, clearError } =
    useAuthStore();
  const passwordRef = useRef<TextInput>(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [googleBusy, setGoogleBusy] = useState(false);

  useFocusEffect(
    useCallback(() => {
      clearError();
    }, [clearError])
  );

  const handleSignIn = async () => {
    if (!email || !password) return;
    try {
      await signIn({ email, password });
    } catch {
      // Error is handled by the auth store
    }
  };

  const handleGoogle = async () => {
    setGoogleBusy(true);
    try {
      await signInWithGoogle();
    } catch {
      // Error surfaces via the store's error state (ErrorBanner above).
    } finally {
      setGoogleBusy(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      appAlert('Enter Email', 'Please enter your email address first, then tap "Forgot password?"');
      return;
    }
    try {
      await sendPasswordResetEmail(email);
      appAlert('Check Your Email', `A password reset link has been sent to ${email}`);
    } catch {
      appAlert('Error', 'Could not send reset email. Please check the address and try again.');
    }
  };

  return (
    <KeyboardAvoidingView
      style={[styles.container, { backgroundColor: colors.background }]}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <Head>
        <title>Sign In - Redi</title>
      </Head>
      <ContentConstraint maxWidth={NARROW_MAX_WIDTH}>
        <ScrollView
          contentContainerStyle={[styles.scrollContent, { paddingTop: insets.top + SPACING.xl }]}
          keyboardShouldPersistTaps="handled">
          {/* Card-less, left-aligned (mockup authwrap): small wordmark, light
              Welcome back, quiet form fields on the bare canvas. */}
          <View style={styles.header}>
            <RediLogo fontSize={20} style={{ marginBottom: SPACING.xl + SPACING.sm }} />
            <Text style={[styles.title, { color: colors.text }]}>Welcome back</Text>
            <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
              Sign in to continue
            </Text>
          </View>

          <View style={styles.form}>
            <Input
              label="Email"
              value={email}
              onChangeText={setEmail}
              placeholder="Enter your email"
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              returnKeyType="next"
              onSubmitEditing={() => passwordRef.current?.focus()}
              blurOnSubmit={false}
            />

            <Input
              ref={passwordRef}
              label="Password"
              value={password}
              onChangeText={setPassword}
              placeholder="Enter your password"
              secureTextEntry
              returnKeyType="done"
              onSubmitEditing={handleSignIn}
            />

            <TouchableOpacity
              style={styles.forgotPassword}
              onPress={handleForgotPassword}
              accessibilityRole="button"
              accessibilityLabel="Forgot password"
              accessibilityHint="Sends a password-reset email to the address you entered above">
              <Text style={[styles.forgotPasswordText, { color: colors.text }]}>
                Forgot password?
              </Text>
            </TouchableOpacity>

            <ErrorBanner message={error} style={styles.errorSpacing} />

            <Button
              title="Sign In"
              onPress={handleSignIn}
              loading={isLoading}
              disabled={!email || !password}
              fullWidth
              size="lg"
            />
          </View>

          <View style={styles.dividerRow}>
            <View style={[styles.dividerLine, { backgroundColor: colors.divider }]} />
            <Text style={[styles.dividerText, { color: colors.textTertiary }]}>or</Text>
            <View style={[styles.dividerLine, { backgroundColor: colors.divider }]} />
          </View>

          {/* Wrapper is web-gated too: the button renders null on native,
              but the wrap's marginBottom would still add phantom spacing. */}
          {Platform.OS === 'web' && (
            <View style={styles.googleButtonWrap}>
              <GoogleSignInButton
                testID="signin-google"
                loading={googleBusy}
                onPress={handleGoogle}
              />
            </View>
          )}

          <View style={styles.footer}>
            <Text style={[styles.footerText, { color: colors.textSecondary }]}>
              Don&apos;t have an account?{' '}
            </Text>
            <Link href="/(auth)/sign-up" asChild>
              <TouchableOpacity accessibilityRole="link" accessibilityLabel="Go to sign up">
                <Text style={[styles.linkText, { color: colors.text }]}>Sign up</Text>
              </TouchableOpacity>
            </Link>
          </View>
        </ScrollView>
      </ContentConstraint>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    padding: SPACING.lg,
  },
  header: {
    alignItems: 'flex-start',
    marginBottom: SPACING.xl,
  },
  title: {
    ...TYPOGRAPHY.screenTitle,
    marginBottom: SPACING.xs,
  },
  subtitle: {
    ...TYPOGRAPHY.bodySmall,
  },
  form: {},
  forgotPassword: {
    alignSelf: 'flex-end',
    marginBottom: SPACING.lg,
    marginTop: -SPACING.sm,
  },
  forgotPasswordText: {
    ...TYPOGRAPHY.label,
  },
  errorSpacing: {
    marginBottom: SPACING.md,
  },
  dividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: SPACING.lg,
  },
  dividerLine: {
    flex: 1,
    height: StyleSheet.hairlineWidth,
  },
  dividerText: {
    ...TYPOGRAPHY.bodySmall,
    marginHorizontal: SPACING.md,
  },
  googleButtonWrap: {
    marginBottom: SPACING.lg,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  footerText: {
    ...TYPOGRAPHY.body,
  },
  linkText: {
    ...withWeight(TYPOGRAPHY.body, 'medium'),
  },
});
